import { ElementRef, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LMGlobalPersonalizationService } from '@states/services';
import * as _ from 'lodash';
import { LMContextService, LMLoggerService, LMUrlUtilService } from '@app/services';
import { LMApplicationMenuBaseDirective } from '@components/applicationmenu/lm-application-menu-base.directive';

@Injectable()
export class LMApplicationMenuService {
  constructor(
    private http: HttpClient,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmLoggerService: LMLoggerService,
    private lmContextService: LMContextService,
    private lmUrlUtilService: LMUrlUtilService
  ) {}

  public lastOpenedMenuId: string;
  public lastOpenedMenu: ElementRef;

  private applicationMenuSpec: Array<any> = [];
  private expandedMenus: Array<string> = [];

  // Cache the entire menu structure
  private allLevelsResponse: LMApplicationMenuSpec;

  public saveAllLevelsResponse(response: LMApplicationMenuSpec): void {
    this.allLevelsResponse = _.cloneDeep(response)
  }

  public getAllLevelsResponse(): LMApplicationMenuSpec {
    return this.allLevelsResponse;
  }

  // TODO: implement
  public loadAllLevels() {
    return true;
  }

  public addExpandedMenus(index: string): void {
    this.expandedMenus = [];

    const indexArray = index.split( '.');
    if (indexArray.length === 1) {
      this.expandedMenus.push(index);
    } else {
      let tempIndex = '';
      for (let i = 0; i < indexArray.length; i++) {
        if (i === 0) {
          tempIndex += indexArray[i];
        } else {
          tempIndex += '.' + indexArray[i];
        }
        this.expandedMenus.push(tempIndex);
      }
    }

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }
  /**
   * Keep track of expanded menus for pagestate
   * @param menuItem: LMMenuItemSpec
   */
  public saveExpandedMenus(menuItem: LMMenuItemSpec): void {
    this.updateApplicationMenuState(this.getApplicationMenuSpec(), menuItem);

    this.expandedMenus = [];
    this.saveApplicationMenuState(this.getApplicationMenuSpec());

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  /**
   * Used to clear when roles change
   */
  public clearExpandedMenus() {
    this.expandedMenus = [];

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  public getExpandedMenus(): Array<string> {
    return this.lmGlobalPersonalizationService.getGlobalPersonalization().expandedMenus;
  }

  public saveLastMenuItemClicked(menuIndex: string) {
    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.lastClickedMenuItem = menuIndex;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  public getLastMenuItemClicked() {
    return this.lmGlobalPersonalizationService.getGlobalPersonalization().lastClickedMenuItem;
  }

  public updateApplicationMenuSpec(ApplicationMenuSpec: Array<any>, key?: string, menu?: Array<any>): void {
    if (this.applicationMenuSpec.length === 0) {
      this.applicationMenuSpec = ApplicationMenuSpec;
    } else {
      let newMenu: Array<any> = ApplicationMenuSpec;
      this._updateApplicationMenuSpec(this.applicationMenuSpec, newMenu, key);
    }
  }

  public getApplicationMenuSpec(): Array<any> {
    return this.applicationMenuSpec;
  }

  public setApplicationMenuSpec(newMenuSpec: Array<any>) {
    this.applicationMenuSpec = newMenuSpec;
  }

  public syncWebappMenu(url: string , applicationMenuComponent: LMApplicationMenuBaseDirective) {
    const urlWebAppName = this.lmUrlUtilService.getWebAppNameFromUrl(url);
    if (urlWebAppName !== this.lmContextService.getWebApp()) {
      this.lmLoggerService.log(
        `webApp change detected: new webApp: ${urlWebAppName}, old webapp: ${this.lmContextService.getWebApp()}`);

      // now switch the menu to the future webapp menu if it is different than the current urls
      const webApps: LMWebAppSpec[] = this.lmContextService.getAccessibleWebApps();
      let urlWebAppSpec = webApps ? webApps.filter(webAppSpec => webAppSpec.webAppName === urlWebAppName) : [];
      if (urlWebAppSpec && urlWebAppSpec.length === 1) {
        this.updateWebappMenu(urlWebAppName, applicationMenuComponent);
      }
    }
  }

  public updateWebappMenu(webAppName: string, applicationMenuComponent: LMApplicationMenuBaseDirective) {
    let accessableWebApps = this.lmContextService.getAccessibleWebApps();
    let urlWebAppSpec: LMWebAppSpec[] = accessableWebApps ? accessableWebApps.filter(webAppSpec => webAppSpec.webAppName === webAppName) : [];
    if (urlWebAppSpec.length === 1) {
      this.lmContextService.updateWebApp(urlWebAppSpec[0]);
      if (applicationMenuComponent) {
        applicationMenuComponent.getApplicationMenuPersonalization();
        applicationMenuComponent._loadMenu(this.lmUrlUtilService.getBannerSpecUrl(), true);
      }
    }
  }

  /**
   * Keep track of menu expanded/selected state
   * Later saved to state so accordions can be manually initialized and toggled
   * @param menuSpec
   * @param menuItem
   */
  private updateApplicationMenuState(menuSpec, menuItem) {
    menuSpec.forEach((ms) => {
      if (menuItem.parentIds && menuItem.parentIds.includes(ms.menuIndex)) {
        ms.childExpanded = true;
        menuItem.parentExpanded = true;
      }

      // Reset menu if parent and child is not expanded
      if ((menuItem.menuIndex !== ms.menuIndex && ms.expanded) && (!menuItem.parentExpanded && !ms.childExpanded)
      ) {
        ms.menu = ms.menu ? [] : undefined;
        ms.expanded = false;
        ms.childExpanded = false;
        ms.parentExpanded = false;
      }

      // Collapse if at the same level and not the currently expanded menuItem
        if ((ms.parentIds && menuItem.parentIds && ms.parentIds[ms.parentIds.length - 1] === menuItem.parentIds[menuItem.parentIds.length - 1])
        && ms.menuIndex !== menuItem.menuIndex
      ) {
        if (ms.menu && ms.menu.length > 0) {
          ms.menu = [];
        }
        ms.expanded = false;
        ms.childExpanded = false;
        ms.parentExpanded = false;
      }

      if (ms.menu && ms.menu.length > 0) {
        this.updateApplicationMenuState(ms.menu, menuItem);
      }
    })
  }

  private saveApplicationMenuState(menuSpec): void {
    menuSpec.forEach((menuSpecItem: LMMenuItemSpec) => {
      if (menuSpecItem.expanded) {
        this.expandedMenus.push(menuSpecItem.menuIndex)
      }

      if (menuSpecItem.menu && menuSpecItem.menu.length > 0) {
        this.saveApplicationMenuState(menuSpecItem.menu);
      }
    })
  }

  private _updateApplicationMenuSpec(menu: Array<any>, newMenu: Array<any>, key: string) {
    for (let i = 0, l = menu.length; i < l; i++) {
      let object = menu[ i ];
      for (let k in object) {
        if (object.hasOwnProperty(k)) {
          if (object[ k ] === key) {
            object.submenu = newMenu;
          } else if (object.submenu) {
            this._updateApplicationMenuSpec(object.submenu, newMenu, key);
          }
        }
      }
    }
  }

  public getLastOpenedMenuId(): string {
    return this.lastOpenedMenuId;
  }

  public setLastOpenedMenuId(menu: string) {
    this.lastOpenedMenuId = menu;
  }

  public getLastOpenedMenu(): ElementRef {
    return this.lastOpenedMenu;
  }

  public setLastOpenedMenu(menu: ElementRef) {
    this.lastOpenedMenu = menu;
  }

  public resetLastOpenedMenu() {
    this.lastOpenedMenu = null;
    this.lastOpenedMenuId = '';
  }

  public getChangePasswordAction(actions, name): LMActionSpec {
    if (!name) { // safety
      return null;
    }

    for (let i = 0; i < actions.length; i++) {
      if (actions[ i ].name === name) {
        return actions[ i ];
      }
    }
    return null;
  }

  public updateActorContext(applicationMenuUrl: string, userContextProperties: {}): Observable<any>  {
    let postContent: any = {
      requestType:    'updateActorContext',
      contextProperties: userContextProperties
    };

    return this.http.post(applicationMenuUrl, postContent).pipe(
      catchError(error => {
        return throwError(error || 'Server error');
      }));
  }

}
